import React, { useEffect, useState, useRef, useCallback } from "react";
import { useParams, useNavigate, useSearchParams, Link } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { canUseStaffPage } from "@/lib/page-permissions";
import { hasClientPrivacy, hiddenClientFields } from "@/lib/client-privacy";
import { useOffline } from "@/context/OfflineContext";
import {
  isThisWeek, formatPhone, normalizePhoneForSave, isAddressValid,
  formatVisitDate, formatVisitDateTime, formatVisitTime,
} from "@/lib/utils";
import db from "@/lib/db";
import { queueOp } from "@/lib/sync";
import {
  ArrowLeft, CheckCircle2, AlertTriangle, Trash2, Plus, Save,
  Users, Calendar, Mail, Phone, Send, ShoppingBag, X, RefreshCw, Shirt, ListOrdered, Monitor, EyeOff, Clock,
} from "lucide-react";
import AddressFields from "@/components/AddressFields";
import ActivitySection from "@/components/ActivitySection";
import ClientStaffNotes from "@/components/ClientStaffNotes";
import ClientSharingTreeStatus from "@/components/ClientSharingTreeStatus";
import {
  AlertDialog,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { DEFAULT_DIETARY_OPTIONS, loadDietaryOptions } from "@/lib/dietary-options";

const COAT_SIZES = ["2T", "3T", "4T", "5/6", "7/8", "XS", "S", "M", "L", "XL", "XXL"];


const RELATIONSHIPS = ["Child", "FosterChild", "Friend", "GrandChild", "GrandParent", "GreatGrandChild", "GreatGrandParent", "Parent", "Sibling", "Spouse"];
const GENDERS = ["Female", "Male", "Non-binary", "Prefer not to say"];
const MONTHS_SHORT = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];

function birthLabel(month, year) {
  if (!year) return null;
  const m = month && month >= 1 && month <= 12 ? MONTHS_SHORT[month - 1] + " " : "";
  return `${m}${year}`;
}

function SuspectAgeBadge() {
  return (
    <span
      data-testid="suspect-age-badge"
      className="inline-flex items-center gap-0.5 text-[10px] font-semibold px-1.5 py-0.5 rounded-full"
      style={{ background: "#FEF9C3", color: "#854D0E" }}
    >
      ⚠ Age suspect
    </span>
  );
}

export default function ClientDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const isNewClient = searchParams.get("new") === "1";
  const { user } = useAuth();
  const canQueue = canUseStaffPage(user, "queue");
  const canKiosk = canUseStaffPage(user, "kiosk");
  const { isOnline } = useOffline();
  const isViewer = user?.role === 'viewer';
  const profileReadOnly = isViewer || hasClientPrivacy(user);
  const [client, setClient] = useState(null);
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);
  const [warning, setWarning] = useState(null);
  const [visits, setVisits] = useState([]);
  const [visitToDelete, setVisitToDelete] = useState(null);
  const [deletingVisit, setDeletingVisit] = useState(false);
  const [deleteVisitError, setDeleteVisitError] = useState("");
  const [portalLinks, setPortalLinks] = useState([]);
  const [portalLinksLoading, setPortalLinksLoading] = useState(false);
  const [dietaryOptions, setDietaryOptions] = useState(DEFAULT_DIETARY_OPTIONS);
  const portalPollRef = useRef(null);
  const addMemberBtnRef = useRef(null);
  const [addMemberHighlight, setAddMemberHighlight] = useState(false);
  const newClientIntroFiredRef = useRef(false);

  // Visit date for backfill (defaults to today)
  const todayStr = () => new Date().toISOString().slice(0, 10);
  const [visitDate, setVisitDate] = useState(todayStr);

  // Bag selection state
  const [bagPanel, setBagPanel] = useState(null); // null | { bags: [], selectedIds: Set, visitDate: string }
  const [bagLoading, setBagLoading] = useState(false);
  const [coatSettings, setCoatSettings] = useState(null);
  const [queueStatus, setQueueStatus] = useState(null); // null | { in_queue, status, position }
  const [addingToQueue, setAddingToQueue] = useState(false);
  const [queueMsg, setQueueMsg] = useState("");
  const [kioskState, setKioskState] = useState(null); // { client_id, visible }
  const [kioskLoading, setKioskLoading] = useState(false);
  const [newMemberId, setNewMemberId] = useState(null);

  async function load() {
    try {
      const [clientRes, visitsRes, coatRes, options] = await Promise.all([
        api.get(`/clients/${id}`),
        api.get(`/clients/${id}/visits`),
        api.get("/coat-settings").catch(() => ({ data: { is_active: false, season_label: "" } })),
        loadDietaryOptions(),
      ]);
      setClient(clientRes.data);
      setVisits(visitsRes.data);
      setCoatSettings(coatRes.data);
      setDietaryOptions(options);
      const record = { ...clientRes.data, _id: clientRes.data.id };
      await db.clients.put(record);
    } catch (err) {
      if (!isOnline) {
        const local = await db.clients.get(id).catch(() => null);
        if (local) {
          setClient({ ...local, id: local.id || local._id });
        } else {
          setError("You're offline and this client isn't in the local cache.");
        }
      } else {
        setError(formatApiError(err));
      }
    }
  }

  async function loadQueueStatus() {
    try {
      const { data } = await api.get(`/checkins/today/client/${id}`);
      setQueueStatus(data);
    } catch { /* non-critical */ }
  }

  useEffect(() => {
    let active = true;
    setKioskState(null);
    if (canKiosk) api.get("/kiosk").then(({ data }) => {
      if (active) setKioskState(data);
    }).catch(() => {});
    return () => { active = false; };
  }, [id, canKiosk]);

  async function addToQueue() {
    if (!canQueue) return;
    setAddingToQueue(true); setQueueMsg(""); setError("");
    try {
      await api.post("/checkins/staff-add", { client_id: id });
      setQueueMsg("Added to today's queue!");
      await loadQueueStatus();
    } catch (e) { setError(formatApiError(e)); }
    finally { setAddingToQueue(false); }
  }

  async function loadPortalLinks() {
    if (hasClientPrivacy(user)) { setPortalLinks([]); return; }
    setPortalLinksLoading(true);
    try {
      const { data } = await api.get(`/clients/${id}/portal-links`);
      setPortalLinks(data);
    } catch { /* non-critical */ }
    finally { setPortalLinksLoading(false); }
  }

  const loadPortalLinksQuiet = useCallback(async () => {
    if (hasClientPrivacy(user)) return;
    try {
      const { data } = await api.get(`/clients/${id}/portal-links`);
      setPortalLinks(data);
    } catch { /* non-critical */ }
  }, [id, user]);

  useEffect(() => { load(); loadPortalLinks(); loadQueueStatus(); }, [id]);

  useEffect(() => {
    portalPollRef.current = setInterval(loadPortalLinksQuiet, 10000);
    return () => clearInterval(portalPollRef.current);
  }, [loadPortalLinksQuiet]);

  useEffect(() => {
    if (!isNewClient || !client || isViewer || newClientIntroFiredRef.current) return;
    const btn = addMemberBtnRef.current;
    if (!btn) return;
    newClientIntroFiredRef.current = true;
    btn.scrollIntoView({ behavior: "smooth", block: "center" });
    setAddMemberHighlight(true);
    const t = setTimeout(() => setAddMemberHighlight(false), 2000);
    navigate(`/staff/clients/${id}`, { replace: true });
    return () => clearTimeout(t);
  }, [isNewClient, client, isViewer, id, navigate]);

  async function saveClient() {
    setError(""); setSaving(true);
    if (!isAddressValid(client.address)) {
      setError("Address is incomplete. Please fill in Street, City, State, and a 5-digit Zip.");
      setSaving(false);
      return;
    }
    const payload = {
      first_name: client.first_name, last_name: client.last_name,
      phone: client.phone, email: client.email, address: client.address,
      gender: client.gender, birth_year: client.birth_year, birth_month: client.birth_month || null,
      address_verified: client.address_verified, verification_note: client.verification_note,
      dietary_restrictions: client.dietary_restrictions || [],
      notify_when_called: client.notify_when_called || false,
      google_verification_token: client.google_verification_token || null,
    };
    if (!isOnline) {
      // Save the address offline, but never queue an expiring verification proof.
      delete payload.google_verification_token;
      try {
        const updated = { ...client, ...payload, _pendingSync: true, google_verification_token: "" };
        await db.clients.put({ ...updated, _id: id });
        await queueOp("update", { id, data: payload });
        setClient((prev) => ({ ...prev, ...payload, _pendingSync: true }));
      } catch (err) { setError("Failed to save offline: " + err.message); }
      finally { setSaving(false); }
      return;
    }
    try {
      const { data } = await api.put(`/clients/${id}`, payload);
      setClient((prev) => ({ ...prev, ...data, google_verification_token: "" }));
      await db.clients.put({ ...data, _id: id });
    } catch (err) { setError(formatApiError(err)); }
    finally { setSaving(false); }
  }

  async function openBagPanel(override = false) {
    setBagLoading(true);
    try {
      const hSize = client.household_size || 1;
      const { data } = await api.get(`/bag-mappings/resolve?household_size=${hSize}`);
      const defaultIds = new Set((data.bags || []).map((b) => b.id));
      setBagPanel({ bags: data.bags || [], selectedIds: defaultIds, override, visitDate });
    } catch {
      setBagPanel({ bags: [], selectedIds: new Set(), override, visitDate });
    } finally { setBagLoading(false); }
  }

  async function confirmCheckIn() {
    if (!bagPanel) return;
    setError("");
    const selectedBagIds = [...bagPanel.selectedIds];

    if (!isOnline) {
      try {
        await queueOp("checkin", {
          clientId: id,
          override: bagPanel.override,
          bag_ids: selectedBagIds,
          visit_date: bagPanel.visitDate || null,
        });
        setClient((prev) => ({ ...prev, visited_this_month: true, _pendingSync: true }));
        const local = await db.clients.get(id).catch(() => null);
        if (local) {
          await db.clients.put({ ...local, visited_this_month: true, _pendingSync: true });
        }
        setBagPanel(null);
        setWarning(null);
      } catch (err) {
        setError("Failed to queue check-in: " + err.message);
      }
      return;
    }

    try {
      await api.post(`/clients/${id}/visits`, {
        override: bagPanel.override,
        bag_ids: selectedBagIds,
        visit_date: bagPanel.visitDate || null,
      });
      setBagPanel(null);
      setWarning(null);
      await load();
    } catch (err) {
      if (err.response?.status === 409) {
        setWarning(err.response.data.detail);
        setBagPanel(null);
      } else {
        setError(formatApiError(err));
      }
    }
  }

  function openDeleteVisit(visit) {
    setDeleteVisitError("");
    setVisitToDelete(visit);
  }

  async function confirmDeleteVisit() {
    if (!visitToDelete || deletingVisit) return;
    setDeletingVisit(true);
    setDeleteVisitError("");
    try {
      await api.delete(`/clients/${id}/visits/${visitToDelete.id}`);
      setVisitToDelete(null);
      await load();
    } catch (err) {
      setDeleteVisitError(formatApiError(err));
    } finally {
      setDeletingVisit(false);
    }
  }

  function toggleBag(bagId) {
    if (!bagPanel) return;
    const next = new Set(bagPanel.selectedIds);
    if (next.has(bagId)) next.delete(bagId);
    else next.add(bagId);
    setBagPanel({ ...bagPanel, selectedIds: next });
  }

  async function addMember() {
    const m = { first_name: "", last_name: "", gender: "", birth_year: null, birth_month: null, relationship: "Child" };
    if (!isOnline) {
      const tempMemberId = `pending_member_${Date.now()}`;
      const newMember = { ...m, id: tempMemberId, _pendingSync: true };
      setClient((prev) => ({
        ...prev,
        household_members: [...(prev.household_members || []), newMember],
        household_size: (prev.household_size || 1) + 1,
      }));
      setNewMemberId(tempMemberId);
      await queueOp("add_member", { clientId: id, data: m, tempId: tempMemberId });
      const local = await db.clients.get(id).catch(() => null);
      if (local) {
        await db.clients.put({
          ...local,
          household_members: [...(local.household_members || []), newMember],
          household_size: (local.household_size || 1) + 1,
        });
      }
      return;
    }
    const { data } = await api.post(`/clients/${id}/members`, m);
    setNewMemberId(data.id);
    await load();
  }

  async function deleteMember(memberId) {
    if (!window.confirm("Remove this member from the client profile? Saved Sharing Tree requests will be kept. To combine duplicate Sharing Tree requests, use “Combine duplicate members” in the Sharing Tree household editor.")) return;
    if (!isOnline) {
      setClient((prev) => ({
        ...prev,
        household_members: (prev.household_members || []).filter((m) => m.id !== memberId),
        household_size: Math.max(1, (prev.household_size || 1) - 1),
      }));
      await queueOp("delete_member", { memberId });
      const local = await db.clients.get(id).catch(() => null);
      if (local) {
        await db.clients.put({
          ...local,
          household_members: (local.household_members || []).filter((m) => m.id !== memberId),
          household_size: Math.max(1, (local.household_size || 1) - 1),
        });
      }
      return;
    }
    await api.delete(`/members/${memberId}`);
    await load();
  }

  async function updateMember(member) {
    const payload = {
      first_name: member.first_name, last_name: member.last_name,
      gender: member.gender, birth_month: member.birth_month, birth_year: member.birth_year,
      relationship: member.relationship,
      phone: member.phone || null,
      email: member.email || null,
    };
    if (!isOnline) {
      setClient((prev) => ({
        ...prev,
        household_members: (prev.household_members || []).map((m) =>
          m.id === member.id ? { ...m, ...payload, _pendingSync: true } : m
        ),
      }));
      await queueOp("update_member", { memberId: member.id, data: payload });
      const local = await db.clients.get(id).catch(() => null);
      if (local) {
        await db.clients.put({
          ...local,
          household_members: (local.household_members || []).map((m) =>
            m.id === member.id ? { ...m, ...payload, _pendingSync: true } : m
          ),
        });
      }
      return;
    }
    await api.put(`/members/${member.id}`, payload);
    await load();
  }

  async function sendMagicLink() {
    if (!client?.email) { setError("Add an email first."); return; }
    try {
      await api.post("/auth/client/request-link", { email: client.email });
      await loadPortalLinks();
    } catch (err) { setError(formatApiError(err)); }
  }

  async function toggleKiosk() {
    if (!canKiosk) return;
    setKioskLoading(true);
    const isOnKioskAndVisible = kioskState?.client_id === id && kioskState?.visible;
    try {
      const { data } = await api.put("/kiosk", { client_id: id, visible: !isOnKioskAndVisible });
      setKioskState(data);
    } catch (err) { setError(formatApiError(err)); }
    finally { setKioskLoading(false); }
  }

  if (!client) {
    return <div className="text-sm" style={{ color: "var(--fb-text-muted)" }}>Loading…</div>;
  }

  const eligible = client.address_verified && !client.visited_this_month;

  const spouseMember = (client.household_members || []).find((m) => m.relationship === "Spouse") || null;
  const householdDisplayName = spouseMember
    ? `${client.first_name} & ${spouseMember.first_name} ${client.last_name}`
    : `${client.first_name} ${client.last_name}`;

  return (
    <div className="space-y-6" data-testid="client-detail-page">
      <button onClick={() => navigate("/staff/clients")} className="inline-flex items-center gap-2 text-sm font-medium"
        style={{ color: "var(--fb-text-muted)" }} data-testid="back-to-clients">
        <ArrowLeft className="w-4 h-4" /> Back to Clients
      </button>

      <div className="flex items-start justify-between gap-4 sm:gap-6 flex-wrap">
        <div>
          <h1 className="text-3xl sm:text-4xl font-bold" style={{ fontFamily: "Outfit" }}>
            {householdDisplayName}
          </h1>
          {(client.birth_year || client.gender) && (
            <div className="mt-1 flex items-center gap-2 flex-wrap text-sm font-medium" style={{ color: "var(--fb-text-muted)" }}>
              {client.birth_year && (() => {
                const age = new Date().getFullYear() - client.birth_year;
                return (
                  <span data-testid="primary-age" className="inline-flex items-center gap-1.5">
                    Age {age}
                    {age > 100 && <SuspectAgeBadge />}
                  </span>
                );
              })()}
              {client.birth_year && client.gender && <span>·</span>}
              {client.gender && <span>{client.gender}</span>}
            </div>
          )}
          <div className="text-sm mt-2 flex items-center gap-4 flex-wrap" style={{ color: "var(--fb-text-muted)" }}>
            <span className="inline-flex items-center gap-1.5"><Users className="w-4 h-4" /> Household of {client.household_size}</span>
            {client.last_visit_date && (
              <span className="inline-flex items-center gap-1.5 flex-wrap">
                <span className="inline-flex items-center gap-1.5"><Calendar className="w-4 h-4" /> Last visit {formatVisitDate(client.last_visit_date)}</span>
                {isThisWeek(client.last_visit_date) && (
                  <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold" style={{ background: "#EDE9FE", color: "#5B21B6" }}>
                    <AlertTriangle className="w-3 h-3" />
                    Visited this week
                  </span>
                )}
              </span>
            )}
            <span className="inline-flex items-center gap-1.5">{client.visit_count_12mo} visits / 12 mo</span>
          </div>
          <ClientSharingTreeStatus clientId={client.id === id ? id : null} />
        </div>

        {/* Kiosk controls — only for staff with write permission */}
        {canKiosk && (
          <div className="flex items-center gap-2 shrink-0" data-testid="kiosk-controls">
            <button
              data-testid="send-to-kiosk-btn"
              onClick={toggleKiosk}
              disabled={kioskLoading}
              className="inline-flex items-center gap-1.5 text-sm font-semibold px-4 py-2 rounded-xl transition-colors disabled:opacity-50"
              style={{
                background: kioskState?.client_id === id && kioskState?.visible ? "var(--fb-primary)" : "var(--fb-primary)15",
                color: kioskState?.client_id === id && kioskState?.visible ? "#fff" : "var(--fb-primary)",
                border: "none",
              }}
              title={kioskState?.client_id === id && kioskState?.visible ? "Hide this client from the kiosk" : "Send this client's info to the kiosk display"}
            >
              {kioskState?.client_id === id && kioskState?.visible ? <EyeOff className="w-4 h-4" /> : <Monitor className="w-4 h-4" />}
              {kioskState?.client_id === id && kioskState?.visible ? "Hide from Kiosk" : "Send to Kiosk"}
            </button>
          </div>
        )}
      </div>

      {(client.import_flags || []).length > 0 && (
        <DuplicateReviewPanel client={client} onChanged={load} isViewer={profileReadOnly} />
      )}

      {/* Check-In panel */}
      <div className="fb-card p-6">
        {isViewer ? (
          <div
            data-testid="checkin-status-readonly"
            className="w-full py-5 sm:py-7 rounded-2xl text-xl sm:text-2xl md:text-3xl font-bold flex items-center justify-center gap-3 sm:gap-4 text-center"
            style={{
              background: client.visited_this_month ? "var(--fb-warning-bg)" : eligible ? "var(--fb-success-bg)" : "var(--fb-error-bg, #FEE2E2)",
              color: client.visited_this_month ? "var(--fb-warning)" : eligible ? "var(--fb-success)" : "var(--fb-error)",
            }}
          >
            {eligible ? <CheckCircle2 className="w-7 h-7 sm:w-8 sm:h-8" /> : <AlertTriangle className="w-7 h-7 sm:w-8 sm:h-8" />}
            {client.visited_this_month ? "Already Visited This Month" : eligible ? "Eligible for Check-In" : "Address Not Verified"}
          </div>
        ) : (
          <>
            {/* Normal check-in button — shows bag panel */}
            {!warning && !bagPanel && (
              <div className="space-y-3">
                <div className="flex items-center gap-3">
                  <label className="text-xs uppercase tracking-wider font-semibold whitespace-nowrap shrink-0" style={{ color: "#8B918B" }}>Visit Date</label>
                  <input
                    data-testid="visit-date-input"
                    type="date"
                    value={visitDate}
                    max={todayStr()}
                    onChange={(e) => setVisitDate(e.target.value)}
                    className="px-3 py-1.5 rounded-lg border outline-none focus:border-[#052F66] text-sm tabular-nums"
                    style={{ borderColor: "var(--fb-border)", background: "#FAFAF8" }}
                  />
                  {visitDate !== todayStr() && (
                    <span className="text-xs font-semibold px-2 py-0.5 rounded-full" style={{ background: "#FEF3C7", color: "#92400E" }}>
                      Backfill
                    </span>
                  )}
                </div>
                <button
                  data-testid="check-in-btn"
                  onClick={() => openBagPanel(false)}
                  disabled={bagLoading}
                  className="w-full py-5 sm:py-7 rounded-2xl text-xl sm:text-2xl md:text-3xl font-bold text-white flex items-center justify-center gap-3 sm:gap-4 transition-transform hover:-translate-y-0.5 shadow-lg text-center"
                  style={{ background: eligible ? "var(--fb-success)" : client.visited_this_month ? "var(--fb-warning)" : "var(--fb-error)" }}
                >
                  {eligible ? <CheckCircle2 className="w-7 h-7 sm:w-8 sm:h-8" /> : <AlertTriangle className="w-7 h-7 sm:w-8 sm:h-8" />}
                  {bagLoading ? "Loading bags…" : client.visited_this_month ? "Already Visited This Month" : eligible ? "Check In Client" : "Address Not Verified"}
                </button>

                {/* Add to Queue / Queue Status */}
                {queueStatus?.in_queue ? (
                  <div
                    data-testid="queue-status-badge"
                    onClick={canQueue ? () => navigate(`/staff/queue?checkinId=${queueStatus.id}`) : undefined}
                    className={`px-4 py-2.5 rounded-xl text-sm font-medium ${canQueue ? "cursor-pointer hover:opacity-80 transition-opacity" : ""}`}
                    style={{ background: "var(--fb-bg)", border: "1.5px solid var(--fb-border)" }}
                    title={canQueue ? "Go to Saturday Queue" : undefined}
                  >
                    <div className="flex items-center justify-between gap-2">
                      <span className="inline-flex items-center gap-2">
                        <ListOrdered className="w-4 h-4 shrink-0" style={{ color: "var(--fb-primary)" }} />
                        In queue today ·{" "}
                        <span className="font-semibold" style={{ color: "var(--fb-primary)" }}>
                          {queueStatus.status === "waiting" && `Waiting (#${queueStatus.position})`}
                          {queueStatus.status === "serving" && "Now serving"}
                          {queueStatus.status === "done" && "Done today"}
                        </span>
                      </span>
                      {canQueue && <span className="text-xs font-semibold underline underline-offset-2" style={{ color: "var(--fb-primary)" }}>
                        View queue
                      </span>}
                    </div>
                    {queueStatus.item_selections?.length > 0 && (
                      <div className="flex flex-wrap gap-1.5 mt-2">
                        {queueStatus.item_selections.map((item) => (
                          <span key={item} className="text-xs px-2 py-0.5 rounded-full font-medium"
                            style={{ background: "var(--fb-primary)15", color: "var(--fb-primary)" }}>
                            {item}
                          </span>
                        ))}
                      </div>
                    )}
                  </div>
                ) : canQueue ? (
                  <div className="flex items-center gap-3">
                    <button
                      data-testid="add-to-queue-btn"
                      onClick={addToQueue}
                      disabled={addingToQueue}
                      className="inline-flex items-center gap-1.5 text-sm font-semibold px-4 py-2.5 rounded-xl border hover:bg-stone-50 disabled:opacity-50"
                      style={{ borderColor: "var(--fb-border)" }}
                    >
                      <ListOrdered className="w-4 h-4" />
                      {addingToQueue ? "Adding…" : "Add to Queue"}
                    </button>
                    {queueMsg && (
                      <span className="text-sm font-medium" style={{ color: "var(--fb-success)" }}>{queueMsg}</span>
                    )}
                  </div>
                ) : null}
              </div>
            )}

            {/* Monthly override warning */}
            {warning && !bagPanel && (
              <div className="space-y-4">
                <div className="p-4 rounded-xl fb-status-warning flex items-start gap-3">
                  <AlertTriangle className="w-5 h-5 mt-0.5 shrink-0" />
                  <div>
                    <div className="font-semibold">{warning.message}</div>
                    <div className="text-sm mt-1">Last visit: {formatVisitDateTime(warning.last_visit)}</div>
                  </div>
                </div>
                <div className="flex gap-3">
                  <button onClick={() => setWarning(null)} className="px-5 py-3 rounded-xl border font-medium" style={{ borderColor: "var(--fb-border)" }}>
                    Cancel
                  </button>
                  <button data-testid="override-checkin-btn" onClick={() => openBagPanel(true)}
                    className="px-5 py-3 rounded-xl font-semibold text-white"
                    style={{ background: "var(--fb-error)" }}>
                    Override and Check In Anyway
                  </button>
                </div>
              </div>
            )}

            {/* Bag selection panel */}
            {bagPanel && (
              <div className="space-y-4">
                {(client.dietary_restrictions || []).length > 0 && (
                  <div className="flex items-start gap-3 p-3 rounded-xl" style={{ background: "#FFF7ED", border: "1px solid #FED7AA" }} data-testid="dietary-checkin-banner">
                    <span className="text-lg leading-none shrink-0">🥗</span>
                    <div>
                      <div className="text-sm font-semibold" style={{ color: "#9A3412" }}>Dietary Restrictions</div>
                      <div className="text-sm mt-0.5" style={{ color: "#C2410C" }}>
                        {(client.dietary_restrictions || []).join(", ")}
                      </div>
                    </div>
                  </div>
                )}
                <BagSelectionPanel
                  bagPanel={bagPanel}
                  allBags={bagPanel.bags}
                  onToggle={toggleBag}
                  onConfirm={confirmCheckIn}
                  onCancel={() => { setBagPanel(null); }}
                  householdSize={client.household_size}
                />
              </div>
            )}
          </>
        )}
      </div>

      <div className="grid lg:grid-cols-3 gap-6">
        {/* Contact / address */}
        <fieldset disabled={profileReadOnly} className="fb-card p-6 lg:col-span-2 space-y-5">
          <h3 className="text-xl font-semibold" style={{ fontFamily: "Outfit" }}>Client Information</h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <LabeledInput label="First Name" value={client.first_name} onChange={(v) => setClient({ ...client, first_name: v })} testid="edit-firstname" readOnly={isViewer} />
            <LabeledInput label="Last Name" value={client.last_name} onChange={(v) => setClient({ ...client, last_name: v })} testid="edit-lastname" readOnly={isViewer} />
            <LabeledInput icon={<Phone className="w-3.5 h-3.5" />} label="Phone" value={formatPhone(client.phone)} onChange={(v) => setClient({ ...client, phone: v })} onBlur={() => setClient((prev) => ({ ...prev, phone: normalizePhoneForSave(prev.phone) }))} testid="edit-phone" readOnly={isViewer} />
            <LabeledInput icon={<Mail className="w-3.5 h-3.5" />} label="Email" value={client.email || ""} onChange={(v) => setClient({ ...client, email: v })} testid="edit-email" readOnly={isViewer} />
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="text-xs uppercase tracking-wider font-semibold mb-1.5 block" style={{ color: "#8B918B" }}>Gender</label>
              <select data-testid="edit-gender" value={client.gender || ""} onChange={(e) => setClient({ ...client, gender: e.target.value })}
                disabled={isViewer}
                className="w-full px-3.5 py-2.5 rounded-lg border outline-none bg-white" style={{ borderColor: "var(--fb-border)" }}>
                <option value="">— Select —</option>
                <option>Female</option><option>Male</option><option>Non-binary</option><option>Prefer not to say</option>
              </select>
            </div>
            <div>
              <label className="text-xs uppercase tracking-wider font-semibold mb-1.5 block" style={{ color: "#8B918B" }}>Age</label>
              <input data-testid="edit-age" type="number" min="0" max="120"
                value={client.birth_year ? new Date().getFullYear() - client.birth_year : ""}
                onChange={(e) => {
                  const a = parseInt(e.target.value);
                  setClient({ ...client, birth_year: isNaN(a) ? null : new Date().getFullYear() - a, birth_month: client.birth_month || 1 });
                }}
                readOnly={isViewer}
                placeholder="Age in years"
                className="w-full px-3.5 py-2.5 rounded-lg border outline-none focus:border-[#052F66] tabular-nums"
                style={{ borderColor: "var(--fb-border)", background: "#FAFAF8" }}
              />
            </div>
          </div>

          <AddressFields value={client.address || ""} onChange={(v) => setClient((c) => ({ ...c, address: v, google_address_verified: false, google_verification_token: "" }))} onVerification={(t) => setClient((c) => ({ ...c, google_verification_token: t || "" }))} readOnly={profileReadOnly} compact />
          {client.google_address_verified && <p className="text-xs font-semibold" style={{ color: "#1E40AF" }} data-testid="google-verified-tag">Google verified address</p>}

          <div className="flex items-center gap-3 p-4 rounded-xl" style={{ background: "var(--fb-bg)" }}>
            <input data-testid="address-verified-checkbox" type="checkbox" checked={client.address_verified || false}
              onChange={(e) => setClient({ ...client, address_verified: e.target.checked })}
              disabled={isViewer}
              className="w-5 h-5 rounded accent-[#052F66]" />
            <div className="flex-1">
              <div className="text-sm font-semibold">Address Verified</div>
              <div className="text-xs" style={{ color: "var(--fb-text-muted)" }}>Confirmed via utility bill, lease, etc.</div>
            </div>
          </div>
          <LabeledInput label="Verification Note" value={client.verification_note || ""} onChange={(v) => setClient({ ...client, verification_note: v })} testid="edit-verification-note" readOnly={isViewer} />

          <div className="flex items-center gap-3 p-4 rounded-xl" style={{ background: "var(--fb-bg)" }}>
            <input data-testid="notify-when-called-checkbox" type="checkbox" checked={client.notify_when_called || false}
              onChange={async (e) => {
                const checked = e.target.checked;
                setClient((prev) => ({ ...prev, notify_when_called: checked }));
                try {
                  await api.put(`/clients/${id}`, { notify_when_called: checked });
                } catch (err) {
                  setClient((prev) => ({ ...prev, notify_when_called: !checked }));
                  setError(formatApiError(err));
                }
              }}
              disabled={isViewer}
              className="w-5 h-5 rounded accent-[#052F66]" />
            <div className="flex-1">
              <div className="text-sm font-semibold">Notify when called</div>
              <div className="text-xs" style={{ color: "var(--fb-text-muted)" }}>Send a "you've been called" email when staff call this client from the Saturday queue.</div>
            </div>
          </div>

          <div>
            <label className="text-xs uppercase tracking-wider font-semibold mb-2 block" style={{ color: "#8B918B" }}>Dietary Restrictions</label>
            {isViewer ? (
              <div className="px-3.5 py-2.5 rounded-lg border text-sm" style={{ borderColor: "var(--fb-border)", background: "var(--fb-bg)" }}>
                {(client.dietary_restrictions || []).length === 0
                  ? <span style={{ color: "var(--fb-text-muted)" }}>None recorded</span>
                  : (client.dietary_restrictions || []).join(", ")}
              </div>
            ) : (
              <div className="flex flex-wrap gap-2" data-testid="dietary-restrictions-edit">
                {[...new Set([...dietaryOptions, ...(client.dietary_restrictions || [])])].map((opt) => {
                  const selected = (client.dietary_restrictions || []).includes(opt);
                  return (
                    <button
                      key={opt}
                      type="button"
                      onClick={() => {
                        const current = client.dietary_restrictions || [];
                        const next = selected ? current.filter((x) => x !== opt) : [...current, opt];
                        setClient({ ...client, dietary_restrictions: next });
                      }}
                      className="px-3 py-1.5 rounded-full text-sm font-medium border-2 transition-colors"
                      style={{
                        borderColor: selected ? "var(--fb-primary)" : "var(--fb-border)",
                        background: selected ? "var(--fb-primary)" : "white",
                        color: selected ? "white" : "var(--fb-text)",
                      }}
                      data-testid={`diet-opt-${opt}`}
                    >
                      {opt}
                    </button>
                  );
                })}
              </div>
            )}
          </div>

          {error && <div className="text-sm fb-status-error p-3 rounded-lg">{error}</div>}

          {!isViewer && (
            <div className="flex justify-end items-center gap-3">
              {client._pendingSync && (
                <span className="inline-flex items-center gap-1 text-xs font-semibold px-2.5 py-1 rounded-full"
                  style={{ background: "#FEF3C7", color: "#92400E" }}>
                  <Clock className="w-3 h-3" /> Pending sync
                </span>
              )}
              <button data-testid="save-client-btn" onClick={saveClient} disabled={saving}
                className="fb-btn-primary px-6 py-3 rounded-xl font-semibold inline-flex items-center gap-2">
                <Save className="w-4 h-4" /> {saving ? "Saving…" : "Save Changes"}
              </button>
            </div>
          )}
        </fieldset>

        {/* Visit history */}
        {/* On desktop, the information card sets the row height, not the visit count. */}
        <div className="min-w-0 lg:relative">
        <div className="fb-card p-6 flex flex-col lg:absolute lg:inset-0">
          <h3 className="text-xl font-semibold mb-4 shrink-0" style={{ fontFamily: "Outfit" }}>Visit History</h3>
          {visits.length === 0 && (
            <div className="text-sm py-8 text-center" style={{ color: "var(--fb-text-muted)" }}>No visits recorded yet.</div>
          )}
          <ul
            className="space-y-3 max-h-[32rem] lg:max-h-none lg:flex-1 lg:min-h-0 overflow-y-auto overscroll-y-contain pr-2 rounded-sm focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[var(--fb-primary)]"
            data-testid="visit-list"
            aria-label="Visit history"
            tabIndex={visits.length > 0 ? 0 : undefined}
          >
            {visits.map((v) => (
              <li key={v.id} className="flex items-start gap-3 pb-3 border-b last:border-0" style={{ borderColor: "var(--fb-border)" }}>
                <div className="w-9 h-9 rounded-full flex items-center justify-center shrink-0"
                  style={{ background: v.override ? "var(--fb-warning-bg)" : "var(--fb-success-bg)" }}>
                  {v.override
                    ? <AlertTriangle className="w-4 h-4" style={{ color: "var(--fb-warning)" }} />
                    : <CheckCircle2 className="w-4 h-4" style={{ color: "var(--fb-success)" }} />}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-semibold">{formatVisitDate(v.visit_date)}</div>
                  <div className="text-xs" style={{ color: "var(--fb-text-muted)" }}>
                    {formatVisitTime(v.visit_date)}
                    {formatVisitTime(v.visit_date) && (v.override || v.recorded_by) && " · "}
                    {v.override && "OVERRIDE · "}{v.recorded_by && `by ${v.recorded_by}`}
                  </div>
                  {v.bags && v.bags.length > 0 && (
                    <div className="text-xs mt-0.5" style={{ color: "var(--fb-primary)" }}>
                      <span className="inline-flex items-center gap-1">
                        <ShoppingBag className="w-3 h-3" />
                        {v.bags.map((b) => b.name).join(" + ")}
                      </span>
                    </div>
                  )}
                  {v.total_weight_lbs > 0 && (
                    <div className="text-xs mt-0.5 tabular-nums" style={{ color: "var(--fb-text-muted)" }}>
                      {v.total_weight_lbs} lbs distributed
                    </div>
                  )}
                </div>
                {!isViewer && (
                  <button
                    type="button"
                    onClick={() => openDeleteVisit(v)}
                    className="p-2 rounded-lg hover:bg-rose-50 shrink-0"
                    aria-label={`Delete visit from ${formatVisitDate(v.visit_date)}`}
                    data-testid={`delete-visit-${v.id}`}
                  >
                    <Trash2 className="w-4 h-4" style={{ color: "var(--fb-error)" }} />
                  </button>
                )}
              </li>
            ))}
          </ul>
        </div>
        </div>

        <DeleteVisitModal
          visit={visitToDelete}
          busy={deletingVisit}
          error={deleteVisitError}
          onCancel={() => {
            if (!deletingVisit) {
              setVisitToDelete(null);
              setDeleteVisitError("");
            }
          }}
          onConfirm={confirmDeleteVisit}
        />
      </div>

      {/* Household members */}
      <div className="fb-card p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-xl font-semibold" style={{ fontFamily: "Outfit" }}>Household Members</h3>
          {!profileReadOnly && (
            <button
              ref={addMemberBtnRef}
              data-testid="add-member-btn"
              onClick={addMember}
              className="inline-flex items-center gap-1.5 text-sm font-semibold px-4 py-2 rounded-lg border hover:bg-stone-50 transition-all duration-300"
              style={{
                borderColor: addMemberHighlight ? "var(--fb-primary)" : "var(--fb-border)",
                background: addMemberHighlight ? "var(--fb-primary)" : undefined,
                color: addMemberHighlight ? "#fff" : undefined,
                boxShadow: addMemberHighlight ? "0 0 0 3px rgba(5,47,102,0.18)" : undefined,
              }}
            >
              <Plus className="w-4 h-4" /> Add member
            </button>
          )}
        </div>
        {client.household_members.length === 0 && (
          <div className="py-8 text-center text-sm" style={{ color: "var(--fb-text-muted)" }}>
            {isViewer ? "No household members." : "No household members. Click \"Add member\" to begin."}
          </div>
        )}
        <div className="space-y-2" data-testid="household-members">
          {client.household_members.length > 0 && (
            <div className="hidden md:grid grid-cols-12 gap-2 px-3 pb-2 text-xs uppercase tracking-wider font-semibold" style={{ color: "#8B918B" }}>
              <div className="col-span-3">First Name</div>
              <div className="col-span-2">Last Name</div>
              <div className="col-span-2">Gender</div>
              <div className="col-span-1 text-center">Age</div>
              <div className="col-span-3">Relationship</div>
              {!profileReadOnly && <div className="col-span-1 text-right">Actions</div>}
            </div>
          )}
          {client.household_members.map((m) => (
            <MemberRow key={m.id} member={m} onSave={updateMember} onDelete={() => deleteMember(m.id)} isViewer={profileReadOnly} autoFocus={m.id === newMemberId} />
          ))}
        </div>
      </div>

      {coatSettings?.is_active && (
        <CoatDistributionCard
          clientId={id}
          children={client.household_members.filter((m) =>
            m.relationship === "Child" || m.relationship === "FosterChild"
          )}
          isViewer={isViewer || hiddenClientFields(user).includes("names")}
          seasonLabel={coatSettings?.season_label || ""}
        />
      )}

      <CoatHistorySection clientId={id} isViewer={isViewer || hiddenClientFields(user).includes("names")} />
      {hiddenClientFields(user).includes("names") && (
        <p className="text-sm" style={{ color: "var(--fb-text-muted)" }}>
          Recording or changing coat pickups requires recipient names and is unavailable while names are hidden.
        </p>
      )}

      <ClientStaffNotes clientId={id} user={user} isOnline={isOnline} />

      <ActivitySection
        targetType="client"
        targetId={id}
        extraQueries={
          (client?.household_members || []).length > 0
            ? [{ targetType: "member", targetId: (client.household_members || []).map((m) => m.id).join(",") }]
            : []
        }
        title="Client Activity"
        limit={30}
      />

      {/* Portal link send history */}
      <div className="fb-card p-6" data-testid="portal-link-history">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between mb-4">
          <div className="flex items-center gap-2 flex-wrap">
            <h3 className="text-xl font-semibold" style={{ fontFamily: "Outfit" }}>Portal Link History</h3>
            <span className="inline-flex items-center gap-1 text-xs font-medium px-2 py-0.5 rounded-full" style={{ background: "#D1FAE5", color: "#065F46" }}>
              <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse" />
              Live
            </span>
          </div>
          <div className="flex items-center gap-2 shrink-0">
            {!isViewer && (
              <button data-testid="send-magic-link-btn" onClick={sendMagicLink}
                disabled={hasClientPrivacy(user)}
                className="px-4 py-2 rounded-xl text-sm font-semibold border inline-flex items-center gap-2 hover:bg-stone-50"
                style={{ borderColor: "var(--fb-border)", color: "var(--fb-text)" }}>
                <Send className="w-4 h-4" /> Send Portal Link
              </button>
            )}
            <button onClick={loadPortalLinks} disabled={portalLinksLoading || hasClientPrivacy(user)}
              className="inline-flex items-center gap-1.5 text-sm font-semibold px-3 py-1.5 rounded-lg border hover:bg-stone-50 disabled:opacity-50"
              style={{ borderColor: "var(--fb-border)" }}>
              <RefreshCw className={`w-3.5 h-3.5 ${portalLinksLoading ? "animate-spin" : ""}`} />
              Refresh
            </button>
          </div>
        </div>
        {portalLinks.length === 0 ? (
          <div className="text-sm py-6 text-center" style={{ color: "var(--fb-text-muted)" }}>
            {hasClientPrivacy(user) ? "Portal links are unavailable for this account’s privacy settings." : "No portal links sent yet."}
          </div>
        ) : (
          <ul className="space-y-2">
            {portalLinks.map((lnk) => (
              <li key={lnk.id} className="flex items-center gap-3 py-2 border-b last:border-0 text-sm" style={{ borderColor: "var(--fb-border)" }}>
                <Mail className="w-4 h-4 shrink-0" style={{ color: "var(--fb-text-muted)" }} />
                <div className="flex-1 min-w-0">
                  <span className="font-medium">{lnk.email}</span>
                  <span className="ml-2 tabular-nums" style={{ color: "var(--fb-text-muted)" }}>
                    {new Date(lnk.created_at).toLocaleString([], { dateStyle: "medium", timeStyle: "short" })}
                  </span>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  <EmailStatusBadge status={lnk.status} used={lnk.used} />
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Email Status Badge
// ---------------------------------------------------------------------------
const STATUS_STYLES = {
  delivered:        { bg: "#D1FAE5", color: "#065F46", label: "Delivered" },
  opened:           { bg: "#D1FAE5", color: "#065F46", label: "Opened" },
  clicked:          { bg: "#D1FAE5", color: "#065F46", label: "Clicked" },
  sent:             { bg: "#FEF9C3", color: "#854D0E", label: "Sent" },
  delivery_delayed: { bg: "#FEF9C3", color: "#854D0E", label: "Delayed" },
  bounced:          { bg: "#FEE2E2", color: "#991B1B", label: "Bounced" },
  complained:       { bg: "#FEE2E2", color: "#991B1B", label: "Complained" },
  unsubscribed:     { bg: "#F3F4F6", color: "#374151", label: "Unsubscribed" },
};

function EmailStatusBadge({ status, used }) {
  const style = status ? STATUS_STYLES[status] : null;
  return (
    <div className="flex items-center gap-1.5" data-testid="email-status-badge">
      {style ? (
        <span className="text-xs font-semibold px-2 py-0.5 rounded-full"
          data-status={status}
          style={{ background: style.bg, color: style.color }}>
          {style.label}
        </span>
      ) : (
        <span className="text-xs font-semibold px-2 py-0.5 rounded-full"
          data-status={status || "pending"}
          style={{ background: "var(--fb-border)", color: "var(--fb-text-muted)" }}>
          {status || "Pending"}
        </span>
      )}
      {used && (
        <span className="text-xs font-semibold px-2 py-0.5 rounded-full"
          style={{ background: "#D1FAE5", color: "#065F46" }}>
          Used
        </span>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Bag Selection Panel
// ---------------------------------------------------------------------------
function BagSelectionPanel({ bagPanel, allBags, onToggle, onConfirm, onCancel, householdSize }) {
  const [allAvailable, setAllAvailable] = useState(allBags);

  useEffect(() => {
    // Load all bags so staff can add any bag even if not in default mapping
    api.get("/bags").then(({ data }) => setAllAvailable(data)).catch(() => {});
  }, []);

  const selectedCount = bagPanel.selectedIds.size;
  const totalWeight = allAvailable
    .filter((b) => bagPanel.selectedIds.has(b.id))
    .reduce((sum, b) => sum + (b.bag_weight_lbs || 0), 0);

  return (
    <div className="space-y-4" data-testid="bag-selection-panel">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-semibold" style={{ fontFamily: "Outfit" }}>Select Bags to Distribute</h3>
          <p className="text-sm" style={{ color: "var(--fb-text-muted)" }}>
            Defaulted for household of {householdSize}. Adjust as needed.
          </p>
        </div>
        <button onClick={onCancel} className="p-2 rounded-lg hover:bg-stone-100" aria-label="Cancel">
          <X className="w-5 h-5" style={{ color: "var(--fb-text-muted)" }} />
        </button>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-3">
        {allAvailable.map((bag) => {
          const selected = bagPanel.selectedIds.has(bag.id);
          return (
            <button
              key={bag.id}
              onClick={() => onToggle(bag.id)}
              data-testid={`bag-toggle-${bag.id}`}
              className="flex items-center gap-3 p-4 rounded-xl border text-left transition-colors"
              style={{
                borderColor: selected ? "var(--fb-primary)" : "var(--fb-border)",
                background: selected ? "var(--fb-primary)10" : "#FAFAF8",
              }}
            >
              <div className="w-8 h-8 rounded-full flex items-center justify-center shrink-0"
                style={{ background: selected ? "var(--fb-primary)" : "var(--fb-border)" }}>
                <ShoppingBag className="w-4 h-4" style={{ color: selected ? "#fff" : "var(--fb-text-muted)" }} />
              </div>
              <div>
                <div className="font-semibold text-sm">{bag.name}</div>
                <div className="text-xs tabular-nums" style={{ color: "var(--fb-text-muted)" }}>{bag.bag_weight_lbs} lbs</div>
              </div>
            </button>
          );
        })}
        {allAvailable.length === 0 && (
          <div className="col-span-3 text-sm text-center py-2" style={{ color: "var(--fb-text-muted)" }}>
            No bags configured. Go to Settings → Food Distribution to set up bags.
          </div>
        )}
      </div>

      <div className="flex items-center justify-between pt-2 border-t" style={{ borderColor: "var(--fb-border)" }}>
        <div className="text-sm tabular-nums" style={{ color: "var(--fb-text-muted)" }}>
          {selectedCount} bag{selectedCount !== 1 ? "s" : ""} · <span className="font-semibold">{totalWeight.toFixed(1)} lbs</span> total
        </div>
        <div className="flex gap-3">
          <button onClick={onCancel} className="px-4 py-2.5 rounded-xl border font-medium text-sm" style={{ borderColor: "var(--fb-border)" }}>
            Cancel
          </button>
          <button
            data-testid="confirm-checkin-btn"
            onClick={onConfirm}
            className="px-5 py-2.5 rounded-xl font-semibold text-sm text-white"
            style={{ background: bagPanel.override ? "var(--fb-error)" : "var(--fb-success)" }}
          >
            {bagPanel.override ? "Override & Check In" : "Confirm Check-In"}
          </button>
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Sub-components
// ---------------------------------------------------------------------------
function MemberRow({ member, onSave, onDelete, isViewer = false, autoFocus = false }) {
  const [m, setM] = useState(member);
  const [dirty, setDirty] = useState(false);
  const firstNameRef = useRef(null);
  function setField(k, v) { setM({ ...m, [k]: v }); setDirty(true); }
  const currentYear = new Date().getFullYear();
  const displayedAge = m.birth_year ? currentYear - m.birth_year : "";
  const isSpouse = m.relationship === "Spouse";

  useEffect(() => {
    if (autoFocus && firstNameRef.current) {
      firstNameRef.current.focus();
    }
  }, [autoFocus]);
  function setAge(ageStr) {
    const ageNum = parseInt(ageStr);
    if (isNaN(ageNum)) setM({ ...m, birth_year: null, birth_month: null });
    else setM({ ...m, birth_year: currentYear - ageNum, birth_month: m.birth_month || 1 });
    setDirty(true);
  }

  return (
    <div className="rounded-xl border p-3 space-y-2" style={{ borderColor: isSpouse ? "var(--fb-primary)" : "var(--fb-border)" }} data-testid={`member-row-${m.id}`}>
      <div className="grid grid-cols-2 md:grid-cols-12 gap-2 items-center">
        <input ref={firstNameRef} value={m.first_name || ""} onChange={(e) => setField("first_name", e.target.value)} placeholder="First name" aria-label="First name"
          disabled={isViewer}
          readOnly={isViewer}
          className="col-span-2 md:col-span-3 px-3 py-2 rounded-lg border outline-none focus:border-[#052F66]"
          style={{ borderColor: "var(--fb-border)", background: isViewer ? "var(--fb-bg)" : undefined }} data-testid={`member-firstname-${m.id}`} />
        <input value={m.last_name || ""} onChange={(e) => setField("last_name", e.target.value)} placeholder="Last name" aria-label="Last name"
          disabled={isViewer}
          readOnly={isViewer}
          className="col-span-2 md:col-span-2 px-3 py-2 rounded-lg border outline-none focus:border-[#052F66]"
          style={{ borderColor: "var(--fb-border)", background: isViewer ? "var(--fb-bg)" : undefined }} />
        <select value={m.gender || ""} onChange={(e) => setField("gender", e.target.value)} aria-label="Gender"
          disabled={isViewer}
          className="col-span-1 md:col-span-2 px-3 py-2 rounded-lg border outline-none bg-white" style={{ borderColor: "var(--fb-border)" }}>
          <option value="">Gender</option>
          {GENDERS.map((g) => <option key={g}>{g}</option>)}
        </select>
        <div className="col-span-1 md:col-span-1">
          <input type="number" value={displayedAge} placeholder="Age" min="0" max="120" aria-label="Age in years"
            onChange={(e) => setAge(e.target.value)}
            disabled={isViewer}
            readOnly={isViewer}
            className="w-full px-3 py-2 rounded-lg border outline-none tabular-nums text-center"
            style={{ borderColor: "var(--fb-border)", background: isViewer ? "var(--fb-bg)" : undefined }} data-testid={`member-age-${m.id}`} />
          {displayedAge > 100 && (
            <div className="flex justify-center mt-0.5">
              <SuspectAgeBadge />
            </div>
          )}
        </div>
        <select value={m.relationship || ""} onChange={(e) => setField("relationship", e.target.value)} aria-label="Relationship"
          disabled={isViewer}
          className="col-span-1 md:col-span-3 px-3 py-2 rounded-lg border outline-none bg-white" style={{ borderColor: "var(--fb-border)" }}>
          <option value="">Relationship</option>
          {RELATIONSHIPS.map((r) => <option key={r}>{r}</option>)}
        </select>
        {!isViewer && (
          <div className="col-span-1 md:col-span-1 flex justify-end gap-1">
            {dirty && (
              <button data-testid={`save-member-${m.id}`} onClick={() => { onSave(m); setDirty(false); }}
                className="p-2 rounded-lg fb-btn-primary" aria-label="Save member">
                <Save className="w-4 h-4 text-white" />
              </button>
            )}
            <button data-testid={`delete-member-${m.id}`} onClick={onDelete} className="p-2 rounded-lg hover:bg-rose-50" aria-label="Delete member">
              <Trash2 className="w-4 h-4" style={{ color: "var(--fb-error)" }} />
            </button>
          </div>
        )}
        {isViewer && isSpouse && (
          <div className="col-span-1 md:col-span-1" />
        )}
      </div>
      {isSpouse && (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 pt-1 border-t" style={{ borderColor: "var(--fb-border)" }}>
          <div className="relative">
            <Phone className="w-3.5 h-3.5 absolute left-3 top-1/2 -translate-y-1/2" style={{ color: "#8B918B" }} />
            <input
              type="tel"
              value={m.phone || ""}
              onChange={(e) => setField("phone", e.target.value)}
              placeholder="Spouse phone"
              aria-label="Spouse phone"
              disabled={isViewer}
              readOnly={isViewer}
              data-testid={`member-phone-${m.id}`}
              className="w-full pl-9 pr-3 py-2 rounded-lg border outline-none focus:border-[#052F66]"
              style={{ borderColor: "var(--fb-border)", background: isViewer ? "var(--fb-bg)" : undefined }}
            />
          </div>
          <div className="relative">
            <Mail className="w-3.5 h-3.5 absolute left-3 top-1/2 -translate-y-1/2" style={{ color: "#8B918B" }} />
            <input
              type="email"
              value={m.email || ""}
              onChange={(e) => setField("email", e.target.value)}
              placeholder="Spouse email"
              aria-label="Spouse email"
              disabled={isViewer}
              readOnly={isViewer}
              data-testid={`member-email-${m.id}`}
              className="w-full pl-9 pr-3 py-2 rounded-lg border outline-none focus:border-[#052F66]"
              style={{ borderColor: "var(--fb-border)", background: isViewer ? "var(--fb-bg)" : undefined }}
            />
          </div>
        </div>
      )}
    </div>
  );
}

function LabeledInput({ label, value, onChange, onBlur, icon, testid, type = "text", readOnly = false }) {
  return (
    <div>
      <label className="text-xs uppercase tracking-wider font-semibold flex items-center gap-1.5 mb-1.5" style={{ color: "#8B918B" }}>
        {icon} {label}
      </label>
      <input data-testid={testid} type={type} value={value} onChange={(e) => onChange(e.target.value)}
        onBlur={onBlur}
        readOnly={readOnly}
        className="w-full px-3.5 py-2.5 rounded-lg border outline-none focus:border-[#052F66] transition-colors"
        style={{ borderColor: "var(--fb-border)", background: readOnly ? "var(--fb-bg)" : "#FAFAF8", cursor: readOnly ? "default" : undefined }} />
    </div>
  );
}

// ---------------------------------------------------------------------------
// Coat Distribution Card
// ---------------------------------------------------------------------------
function CoatDistributionCard({ clientId, children, isViewer, seasonLabel }) {
  const [coats, setCoats] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ child_name: "", child_age: "", size: COAT_SIZES[0], quantity: 1, notes: "" });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [deletingId, setDeletingId] = useState(null);

  const loadCoats = useCallback(async () => {
    try {
      const { data } = await api.get(`/clients/${clientId}/coats`);
      setCoats(data);
    } catch { /* non-critical */ }
    finally { setLoading(false); }
  }, [clientId]);

  useEffect(() => { loadCoats(); }, [loadCoats]);

  function resetForm() {
    setForm({ child_name: "", child_age: "", size: COAT_SIZES[0], quantity: 1, notes: "" });
    setError("");
    setShowForm(false);
  }

  async function handleSave(e) {
    e.preventDefault();
    if (!form.child_name.trim()) { setError("Please select or enter a child name."); return; }
    setSaving(true); setError("");
    try {
      await api.post(`/clients/${clientId}/coats`, {
        child_name: form.child_name.trim(),
        child_age: form.child_age ? parseInt(form.child_age) : null,
        size: form.size,
        quantity: parseInt(form.quantity) || 1,
        notes: form.notes,
      });
      await loadCoats();
      resetForm();
    } catch (err) { setError(formatApiError(err)); }
    finally { setSaving(false); }
  }

  async function handleDelete(coatId, childName) {
    if (!window.confirm(`Remove coat record for ${childName}?`)) return;
    setDeletingId(coatId);
    try {
      await api.delete(`/coats/${coatId}`);
      await loadCoats();
    } catch (err) { setError(formatApiError(err)); }
    finally { setDeletingId(null); }
  }

  const hasChildren = children.length > 0;

  return (
    <div className="fb-card p-6" data-testid="coat-distribution-card">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-3">
          <div className="w-9 h-9 rounded-xl flex items-center justify-center shrink-0"
            style={{ background: "var(--fb-primary)15", color: "var(--fb-primary)" }}>
            <Shirt className="w-5 h-5" />
          </div>
          <div>
            <h3 className="text-xl font-semibold" style={{ fontFamily: "Outfit" }}>Coat Distribution</h3>
            {seasonLabel && (
              <div className="text-xs font-medium" style={{ color: "var(--fb-text-muted)" }}>
                Season {seasonLabel}
              </div>
            )}
          </div>
        </div>
        {!isViewer && hasChildren && !showForm && (
          <button
            data-testid="add-coat-btn"
            onClick={() => setShowForm(true)}
            className="inline-flex items-center gap-1.5 text-sm font-semibold px-4 py-2 rounded-lg border hover:bg-stone-50"
            style={{ borderColor: "var(--fb-border)" }}
          >
            <Plus className="w-4 h-4" /> Record Coat Pickup
          </button>
        )}
      </div>

      {error && <div className="text-sm fb-status-error p-3 rounded-lg mb-3">{error}</div>}

      {/* Ineligible — no children on file */}
      {!hasChildren && (
        <div className="flex items-start gap-3 p-4 rounded-xl fb-status-warning text-sm" data-testid="coat-no-children">
          <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" />
          <div>
            <div className="font-semibold">No children on record — not eligible for coats.</div>
            <div className="mt-0.5" style={{ color: "inherit", opacity: 0.8 }}>
              Add a household member with relationship "Child" or "Foster Child" to enable coat pickup recording.
            </div>
          </div>
        </div>
      )}

      {/* Add Coat Form */}
      {showForm && !isViewer && (
        <form onSubmit={handleSave} className="mb-5 p-4 rounded-xl border space-y-3" style={{ borderColor: "var(--fb-primary)40", background: "var(--fb-primary)05" }} data-testid="coat-form">
          <div className="text-sm font-semibold" style={{ color: "var(--fb-primary)" }}>New Coat Entry</div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <label className="text-xs uppercase tracking-wider font-semibold mb-1.5 block" style={{ color: "#8B918B" }}>
                Child <span style={{ color: "var(--fb-error)" }}>*</span>
              </label>
              {children.length > 0 ? (
                <select
                  data-testid="coat-child-select"
                  value={form.child_name}
                  onChange={(e) => {
                    const chosen = children.find((c) => `${c.first_name} ${c.last_name}`.trim() === e.target.value);
                    const age = chosen?.birth_year ? new Date().getFullYear() - chosen.birth_year : "";
                    setForm({ ...form, child_name: e.target.value, child_age: age });
                  }}
                  className="w-full px-3.5 py-2.5 rounded-lg border outline-none bg-white"
                  style={{ borderColor: "var(--fb-border)" }}
                  required
                >
                  <option value="">— Select child —</option>
                  {children.map((c) => {
                    const label = `${c.first_name} ${c.last_name}`.trim();
                    const age = c.birth_year ? ` (age ${new Date().getFullYear() - c.birth_year})` : "";
                    return <option key={c.id} value={label}>{label}{age}</option>;
                  })}
                </select>
              ) : (
                <input
                  data-testid="coat-child-name"
                  value={form.child_name}
                  onChange={(e) => setForm({ ...form, child_name: e.target.value })}
                  placeholder="Child name"
                  className="w-full px-3.5 py-2.5 rounded-lg border outline-none"
                  style={{ borderColor: "var(--fb-border)", background: "#FAFAF8" }}
                  required
                />
              )}
            </div>
            <div>
              <label className="text-xs uppercase tracking-wider font-semibold mb-1.5 block" style={{ color: "#8B918B" }}>Size</label>
              <select
                data-testid="coat-size-select"
                value={form.size}
                onChange={(e) => setForm({ ...form, size: e.target.value })}
                className="w-full px-3.5 py-2.5 rounded-lg border outline-none bg-white"
                style={{ borderColor: "var(--fb-border)" }}
              >
                {COAT_SIZES.map((s) => <option key={s}>{s}</option>)}
              </select>
            </div>
            <div>
              <label className="text-xs uppercase tracking-wider font-semibold mb-1.5 block" style={{ color: "#8B918B" }}>Quantity</label>
              <input
                data-testid="coat-quantity"
                type="number" min="1" max="10"
                value={form.quantity}
                onChange={(e) => setForm({ ...form, quantity: e.target.value })}
                className="w-full px-3.5 py-2.5 rounded-lg border outline-none tabular-nums"
                style={{ borderColor: "var(--fb-border)", background: "#FAFAF8" }}
              />
            </div>
            <div>
              <label className="text-xs uppercase tracking-wider font-semibold mb-1.5 block" style={{ color: "#8B918B" }}>Notes</label>
              <input
                data-testid="coat-notes"
                value={form.notes}
                onChange={(e) => setForm({ ...form, notes: e.target.value })}
                placeholder="Optional"
                className="w-full px-3.5 py-2.5 rounded-lg border outline-none"
                style={{ borderColor: "var(--fb-border)", background: "#FAFAF8" }}
              />
            </div>
          </div>
          <div className="flex justify-end gap-3">
            <button type="button" onClick={resetForm} className="px-4 py-2.5 rounded-lg border text-sm" style={{ borderColor: "var(--fb-border)" }}>
              Cancel
            </button>
            <button
              data-testid="coat-save-btn"
              type="submit"
              disabled={saving}
              className="fb-btn-primary px-5 py-2.5 rounded-lg font-semibold text-sm"
            >
              {saving ? "Saving…" : "Save Coat Entry"}
            </button>
          </div>
        </form>
      )}

      {/* Coat records list */}
      {loading ? (
        <div className="text-sm py-4 text-center" style={{ color: "var(--fb-text-muted)" }}>Loading…</div>
      ) : coats.length === 0 ? (
        hasChildren && (
          <div className="text-sm py-6 text-center" style={{ color: "var(--fb-text-muted)" }} data-testid="coat-empty">
            No coats recorded yet this season.
          </div>
        )
      ) : (
        <div className="space-y-2" data-testid="coat-list">
          <div className="hidden sm:grid grid-cols-12 gap-2 px-3 pb-1.5 text-xs uppercase tracking-wider font-semibold" style={{ color: "#8B918B" }}>
            <div className="col-span-3">Child</div>
            <div className="col-span-2">Size</div>
            <div className="col-span-1 text-center">Qty</div>
            <div className="col-span-3">Date</div>
            <div className="col-span-2">By</div>
            {!isViewer && <div className="col-span-1" />}
          </div>
          {coats.map((c) => (
            <div key={c.id} className="grid grid-cols-2 sm:grid-cols-12 gap-2 items-center p-3 rounded-xl border" style={{ borderColor: "var(--fb-border)" }} data-testid={`coat-row-${c.id}`}>
              <div className="col-span-2 sm:col-span-3 font-semibold text-sm">
                {c.child_name}
                {c.child_age != null && <span className="font-normal text-xs ml-1" style={{ color: "var(--fb-text-muted)" }}>(age {c.child_age})</span>}
              </div>
              <div className="sm:col-span-2 text-sm">
                <span className="px-2 py-0.5 rounded-full text-xs font-semibold" style={{ background: "var(--fb-primary)15", color: "var(--fb-primary)" }}>{c.size}</span>
              </div>
              <div className="sm:col-span-1 text-sm font-semibold text-center tabular-nums">{c.quantity}</div>
              <div className="sm:col-span-3 text-sm tabular-nums" style={{ color: "var(--fb-text-muted)" }}>
                {new Date(c.recorded_at).toLocaleDateString()}
              </div>
              <div className="sm:col-span-2 text-xs truncate" style={{ color: "var(--fb-text-muted)" }}>{c.recorded_by}</div>
              {!isViewer && (
                <div className="sm:col-span-1 flex justify-end">
                  <button
                    data-testid={`delete-coat-${c.id}`}
                    onClick={() => handleDelete(c.id, c.child_name)}
                    disabled={deletingId === c.id}
                    className="p-1.5 rounded-lg hover:bg-rose-50 disabled:opacity-50"
                    aria-label="Delete coat record"
                  >
                    <Trash2 className="w-4 h-4" style={{ color: "var(--fb-error)" }} />
                  </button>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Coat History Section — all seasons, always shown when records exist
// ---------------------------------------------------------------------------
function CoatHistorySection({ clientId, isViewer }) {
  const [coats, setCoats] = useState([]);
  const [loading, setLoading] = useState(true);
  const [deletingId, setDeletingId] = useState(null);
  const [error, setError] = useState("");

  const loadHistory = useCallback(async () => {
    try {
      const { data } = await api.get(`/clients/${clientId}/coats?all=true`);
      setCoats(data);
    } catch { /* non-critical */ }
    finally { setLoading(false); }
  }, [clientId]);

  useEffect(() => { loadHistory(); }, [loadHistory]);

  async function handleDelete(coatId, childName) {
    if (!window.confirm(`Remove coat record for ${childName}?`)) return;
    setDeletingId(coatId); setError("");
    try {
      await api.delete(`/coats/${coatId}`);
      await loadHistory();
    } catch (err) { setError(formatApiError(err)); }
    finally { setDeletingId(null); }
  }

  if (loading || coats.length === 0) return null;

  return (
    <div className="fb-card p-6" data-testid="coat-history-section">
      <div className="flex items-center gap-3 mb-4">
        <div className="w-9 h-9 rounded-xl flex items-center justify-center shrink-0"
          style={{ background: "var(--fb-primary)15", color: "var(--fb-primary)" }}>
          <Shirt className="w-5 h-5" />
        </div>
        <h3 className="text-xl font-semibold" style={{ fontFamily: "Outfit" }}>Coat History</h3>
      </div>

      {error && <div className="text-sm fb-status-error p-3 rounded-lg mb-3">{error}</div>}

      {(() => {
        const seasonMap = {};
        for (const c of coats) {
          const key = c.season || "—";
          if (!seasonMap[key]) seasonMap[key] = [];
          seasonMap[key].push(c);
        }
        const seasons = Object.keys(seasonMap).sort((a, b) => {
          if (a === "—") return 1;
          if (b === "—") return -1;
          return b.localeCompare(a);
        });
        return (
          <div className="space-y-4" data-testid="coat-history-list">
            {seasons.map((season) => {
              const group = seasonMap[season];
              const totalCoats = group.reduce((sum, c) => sum + (c.quantity || 1), 0);
              return (
                <div key={season} data-testid={`coat-season-group-${season}`}>
                  <div
                    className="flex items-center justify-between px-3 py-2 rounded-xl mb-2"
                    style={{ background: "var(--fb-primary)0D" }}
                    data-testid={`coat-season-header-${season}`}
                  >
                    <span className="text-sm font-semibold" style={{ color: "var(--fb-primary)" }}>
                      {season}
                    </span>
                    <span className="text-xs font-semibold px-2.5 py-0.5 rounded-full"
                      style={{ background: "var(--fb-primary)20", color: "var(--fb-primary)" }}>
                      {totalCoats} coat{totalCoats !== 1 ? "s" : ""}
                    </span>
                  </div>
                  <div className="space-y-2">
                    <div className="hidden sm:grid grid-cols-12 gap-2 px-3 pb-1 text-xs uppercase tracking-wider font-semibold" style={{ color: "#8B918B" }}>
                      <div className="col-span-3">Child</div>
                      <div className="col-span-2">Size</div>
                      <div className="col-span-1 text-center">Qty</div>
                      <div className="col-span-3">Date</div>
                      {!isViewer && <div className="col-span-3" />}
                    </div>
                    {group.map((c) => (
                      <div key={c.id} className="grid grid-cols-2 sm:grid-cols-12 gap-2 items-center p-3 rounded-xl border"
                        style={{ borderColor: "var(--fb-border)" }} data-testid={`coat-history-row-${c.id}`}>
                        <div className="col-span-2 sm:col-span-3 font-semibold text-sm">
                          {c.child_name}
                          {c.child_age != null && (
                            <span className="font-normal text-xs ml-1" style={{ color: "var(--fb-text-muted)" }}>(age {c.child_age})</span>
                          )}
                        </div>
                        <div className="sm:col-span-2 text-sm">
                          <span className="px-2 py-0.5 rounded-full text-xs font-semibold"
                            style={{ background: "var(--fb-primary)15", color: "var(--fb-primary)" }}>
                            {c.size}
                          </span>
                        </div>
                        <div className="sm:col-span-1 text-sm font-semibold text-center tabular-nums">{c.quantity}</div>
                        <div className="sm:col-span-3 text-sm tabular-nums" style={{ color: "var(--fb-text-muted)" }}>
                          {new Date(c.recorded_at).toLocaleDateString()}
                        </div>
                        {!isViewer && (
                          <div className="sm:col-span-3 flex justify-end">
                            <button
                              data-testid={`delete-coat-history-${c.id}`}
                              onClick={() => handleDelete(c.id, c.child_name)}
                              disabled={deletingId === c.id}
                              className="p-1.5 rounded-lg hover:bg-rose-50 disabled:opacity-50"
                              aria-label="Delete coat record"
                            >
                              <Trash2 className="w-4 h-4" style={{ color: "var(--fb-error)" }} />
                            </button>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
        );
      })()}
    </div>
  );
}

function DeleteVisitModal({ visit, onConfirm, onCancel, busy, error }) {
  if (!visit) return null;
  const date = formatVisitDate(visit.visit_date);
  const time = formatVisitTime(visit.visit_date);
  return (
    <AlertDialog
      open
      onOpenChange={(open) => {
        if (!open && !busy) onCancel();
      }}
    >
      <AlertDialogContent
        className="fb-card w-[calc(100%-2rem)] max-w-md"
        onEscapeKeyDown={(event) => {
          if (busy) event.preventDefault();
        }}
        data-testid="delete-visit-modal"
      >
        <AlertDialogTitle style={{ fontFamily: "Outfit" }}>
          Delete visit?
        </AlertDialogTitle>
        <AlertDialogDescription asChild>
          <div>
            <p className="text-sm font-semibold mb-2" style={{ color: "var(--fb-text)" }}>
              {date}{time ? ` at ${time}` : ""}
            </p>
            <p className="text-sm" style={{ color: "var(--fb-text-muted)" }}>
              This permanently removes the visit from history and reporting. This action cannot be undone.
            </p>
          </div>
        </AlertDialogDescription>
        {error && (
          <div className="text-sm p-3 rounded-lg" role="alert" style={{ background: "var(--fb-error-bg, #FEE2E2)", color: "var(--fb-error)" }}>
            {error}
          </div>
        )}
        <AlertDialogFooter>
          <AlertDialogCancel
            disabled={busy}
            className="text-sm font-semibold disabled:opacity-50"
            data-testid="delete-visit-cancel"
          >
            Cancel
          </AlertDialogCancel>
          <button
            type="button"
            onClick={onConfirm}
            disabled={busy}
            className="text-sm font-semibold px-4 py-2 rounded-lg text-white disabled:opacity-50"
            style={{ background: "var(--fb-error)" }}
            data-testid="delete-visit-confirm"
          >
            {busy ? "Deleting…" : "Delete visit"}
          </button>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}

function MergeConfirmModal({ modal, onConfirm, onCancel, busy }) {
  if (!modal) return null;
  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center"
      style={{ background: "rgba(0,0,0,0.45)" }}
      data-testid="merge-confirm-overlay"
    >
      <div
        className="fb-card p-6 w-full max-w-md mx-4 shadow-xl"
        role="dialog"
        aria-modal="true"
        aria-labelledby="merge-modal-title"
        data-testid="merge-confirm-modal"
      >
        <h2 id="merge-modal-title" className="text-base font-semibold mb-1" style={{ fontFamily: "Outfit" }}>
          {modal.title}
        </h2>
        <div className="text-xs font-medium mb-3" style={{ color: "var(--fb-text-muted)" }}>
          <span className="font-semibold" style={{ color: "var(--fb-text)" }}>{modal.thisClient}</span>
          {" (this page)"}&ensp;·&ensp;
          <span className="font-semibold" style={{ color: "var(--fb-text)" }}>{modal.otherClient}</span>
          {" (possible duplicate)"}
        </div>
        <p className="text-sm mb-5" style={{ color: "var(--fb-text)" }}>
          {modal.description}
        </p>
        <div className="flex gap-3 justify-end">
          <button
            onClick={onCancel}
            disabled={busy}
            className="text-sm font-semibold px-4 py-2 rounded-lg border hover:bg-stone-50 disabled:opacity-50"
            style={{ borderColor: "var(--fb-border)", color: "var(--fb-text)" }}
            data-testid="merge-modal-cancel"
          >
            Cancel
          </button>
          <button
            onClick={onConfirm}
            disabled={busy}
            className="text-sm font-semibold px-4 py-2 rounded-lg text-white disabled:opacity-50"
            style={{ background: "var(--fb-error)" }}
            data-testid="merge-modal-confirm"
          >
            {busy ? "Working…" : modal.confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
}

function DuplicateReviewPanel({ client, onChanged, isViewer = false }) {
  const [dups, setDups] = useState([]);
  const [loadingDups, setLoadingDups] = useState(false);
  const [memberDups, setMemberDups] = useState([]);
  const [loadingMemberDups, setLoadingMemberDups] = useState(false);
  const [dismissingMemberPair, setDismissingMemberPair] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [dismissing, setDismissing] = useState(false);
  const [dismissingAddress, setDismissingAddress] = useState(false);
  const [confirmModal, setConfirmModal] = useState(null);
  const pendingAction = useRef(null);
  const navigate = useNavigate();
  const flags = client.import_flags || [];

  useEffect(() => {
    if (!flags.includes("possible_duplicate")) { setDups([]); return; }
    setLoadingDups(true);
    (async () => {
      try {
        const params = new URLSearchParams({
          first_name: client.first_name || "",
          last_name: client.last_name || "",
          phone: client.phone || "",
          address: client.address || "",
          exclude_id: client.id,
        });
        const { data } = await api.get(`/clients-duplicates?${params}`);
        setDups(data);
      } catch { /* ignore */ }
      finally { setLoadingDups(false); }
    })();
    // eslint-disable-next-line
  }, [client.id]);

  useEffect(() => {
    if (!flags.includes("possible_member_duplicate")) { setMemberDups([]); return; }
    setLoadingMemberDups(true);
    (async () => {
      try {
        const { data } = await api.get(`/clients/${client.id}/member-duplicates`);
        setMemberDups(data);
      } catch { /* ignore */ }
      finally { setLoadingMemberDups(false); }
    })();
    // eslint-disable-next-line
  }, [client.id]);

  async function dismissDuplicate() {
    setDismissing(true); setError("");
    try {
      await api.delete(`/clients/${client.id}/flags/possible_duplicate`);
      onChanged();
    } catch (e) { setError(formatApiError(e)); }
    finally { setDismissing(false); }
  }

  async function resolveIncompleteAddress() {
    setDismissingAddress(true); setError("");
    try {
      await api.delete(`/clients/${client.id}/flags/incomplete_address`);
      onChanged();
    } catch (e) { setError(formatApiError(e)); }
    finally { setDismissingAddress(false); }
  }

  async function dismissMemberDuplicate(otherClientId) {
    const key = otherClientId || "stale";
    setDismissingMemberPair(key); setError("");
    try {
      const qs = otherClientId ? `?other_client_id=${otherClientId}` : "";
      await api.delete(`/clients/${client.id}/flags/possible_member_duplicate${qs}`);
      if (otherClientId) {
        setMemberDups((prev) => prev.filter((m) => m.other_client_id !== otherClientId));
      }
      onChanged();
    } catch (e) { setError(formatApiError(e)); }
    finally { setDismissingMemberPair(null); }
  }

  function thisName() {
    return `${client.first_name || ""} ${client.last_name || ""}`.trim() || "This client";
  }

  function openMergeModal(dup, intoCurrent) {
    const thisN = thisName();
    const otherN = `${dup.first_name || ""} ${dup.last_name || ""}`.trim() || "Other client";
    if (intoCurrent) {
      setConfirmModal({
        title: "Keep this client",
        thisClient: thisN,
        otherClient: otherN,
        description: `${otherN}'s visit history and records will be moved onto ${thisN}'s profile. ${otherN} will be permanently deleted.`,
        confirmLabel: `Yes, keep ${thisN}`,
      });
    } else {
      setConfirmModal({
        title: "Keep other client",
        thisClient: thisN,
        otherClient: otherN,
        description: `${thisN}'s visit history and records will be moved onto ${otherN}'s profile. ${thisN} will be permanently deleted. You'll be taken to ${otherN}'s page.`,
        confirmLabel: `Yes, keep ${otherN}`,
      });
    }
    pendingAction.current = async () => {
      setBusy(true); setError("");
      try {
        const from = intoCurrent ? dup.id : client.id;
        const into = intoCurrent ? client.id : dup.id;
        await api.post(`/clients/${from}/merge-into/${into}`);
        if (!intoCurrent) navigate(`/staff/clients/${into}`, { replace: true });
        else onChanged();
      } catch (e) { setError(formatApiError(e)); }
      finally { setBusy(false); }
    };
  }

  function openDropModal(dup) {
    const thisN = thisName();
    const otherN = `${dup.first_name || ""} ${dup.last_name || ""}`.trim() || "Other client";
    setConfirmModal({
      title: "Delete other (no merge)",
      thisClient: thisN,
      otherClient: otherN,
      description: `${otherN} and all their records will be permanently deleted. No data will be transferred.`,
      confirmLabel: `Yes, delete ${otherN}`,
    });
    pendingAction.current = async () => {
      setBusy(true); setError("");
      try {
        await api.delete(`/clients/${dup.id}`);
        setDups((prev) => prev.filter((x) => x.id !== dup.id));
        onChanged();
      } catch (e) { setError(formatApiError(e)); }
      finally { setBusy(false); }
    };
  }

  async function handleModalConfirm() {
    const action = pendingAction.current;
    setConfirmModal(null);
    pendingAction.current = null;
    if (action) await action();
  }

  function handleModalCancel() {
    setConfirmModal(null);
    pendingAction.current = null;
  }

  return (
    <>
      <MergeConfirmModal
        modal={confirmModal}
        onConfirm={handleModalConfirm}
        onCancel={handleModalCancel}
        busy={busy}
      />
      <div className="fb-card p-5 border-2" style={{ borderColor: "var(--fb-warning)" }} data-testid="duplicate-review-panel">
        <div className="flex items-start justify-between gap-3 mb-3">
          <div className="flex items-start gap-3">
            <div className="w-9 h-9 rounded-lg flex items-center justify-center shrink-0 fb-status-warning">
              <AlertTriangle className="w-4 h-4" />
            </div>
            <div>
              <h3 className="text-lg font-semibold" style={{ fontFamily: "Outfit" }}>Needs review</h3>
              <div className="flex flex-wrap gap-1.5 mt-1">
                {flags.includes("possible_duplicate") && <span className="text-xs px-2 py-0.5 rounded-full fb-status-warning font-semibold">Possible duplicate</span>}
                {flags.includes("possible_member_duplicate") && <span className="text-xs px-2 py-0.5 rounded-full fb-status-warning font-semibold" data-testid="member-dup-badge">Possible duplicate children</span>}
                {flags.includes("incomplete_phone") && <span className="text-xs px-2 py-0.5 rounded-full font-semibold" style={{ background: "#F0E9DB", color: "#6B5A1E" }}>Incomplete phone</span>}
                {flags.includes("incomplete_address") && <span className="text-xs px-2 py-0.5 rounded-full font-semibold" style={{ background: "#F0E9DB", color: "#6B5A1E" }}>Incomplete address</span>}
              </div>
              {flags.includes("incomplete_address") && (
                <div className="mt-2 flex items-center gap-3 flex-wrap">
                  <span className="text-xs" style={{ color: "#6B5A1E" }} data-testid="address-flag-reason">
                    {client.address_flag_reason || "Address information is incomplete"}
                  </span>
                  {!client.address_flag_reason && !isViewer && (
                    <button
                      data-testid="resolve-address-btn"
                      onClick={resolveIncompleteAddress}
                      disabled={dismissingAddress || busy}
                      className="text-xs font-semibold px-3 py-1 rounded-lg border hover:bg-stone-50 disabled:opacity-50"
                      style={{ borderColor: "var(--fb-border)", color: "var(--fb-text)" }}
                    >
                      {dismissingAddress ? "Clearing…" : "Mark as resolved"}
                    </button>
                  )}
                </div>
              )}
            </div>
          </div>
          {flags.includes("possible_duplicate") && !isViewer && (
            <button
              data-testid="not-duplicate-btn"
              onClick={dismissDuplicate}
              disabled={dismissing || busy}
              className="shrink-0 text-xs font-semibold px-3 py-1.5 rounded-lg border hover:bg-stone-50 disabled:opacity-50"
              style={{ borderColor: "var(--fb-border)", color: "var(--fb-text)" }}
            >
              {dismissing ? "Clearing…" : "Not a duplicate"}
            </button>
          )}
        </div>

        {error && <div className="text-sm fb-status-error p-3 rounded-lg mb-3">{error}</div>}

        {flags.includes("possible_duplicate") && dups.length > 0 && (
          <div className="space-y-2">
            <div className="text-xs uppercase tracking-wider font-semibold" style={{ color: "#8B918B" }}>
              Possible duplicate matches:
            </div>
            {dups.map((d) => (
              <div key={d.id} className="flex items-start justify-between gap-3 p-3 rounded-xl border" style={{ borderColor: "var(--fb-border)", background: "#FAFAF8" }} data-testid={`dup-row-${d.id}`}>
                <div className="text-sm min-w-0 flex-1">
                  <Link
                    to={`/staff/clients/${d.id}`}
                    className="font-semibold truncate block hover:underline"
                    style={{ color: "var(--fb-primary)" }}
                    data-testid={`dup-link-${d.id}`}
                  >
                    {d.first_name} {d.last_name}
                  </Link>
                  <div className="text-xs truncate" style={{ color: "var(--fb-text-muted)" }}>
                    {[d.address, d.phone ? formatPhone(d.phone) : null, d.email].filter(Boolean).join(" · ") || "—"}
                  </div>
                </div>
                <div className="flex flex-col gap-1.5 shrink-0">
                  <Link
                    to={`/staff/clients/compare?a=${client.id}&b=${d.id}`}
                    className="text-xs font-semibold px-3 py-1.5 rounded border text-center"
                    style={{ borderColor: "var(--fb-primary)", color: "var(--fb-primary)", textDecoration: "none" }}
                    data-testid={`compare-${d.id}`}
                  >
                    Compare households
                  </Link>
                  {!isViewer && (
                    <>
                      <button onClick={() => openMergeModal(d, true)} disabled={busy}
                              className="text-xs font-semibold px-3 py-1.5 rounded fb-btn-primary text-white"
                              data-testid={`merge-into-current-${d.id}`}>
                        Keep this client
                      </button>
                      <button onClick={() => openMergeModal(d, false)} disabled={busy}
                              className="text-xs font-semibold px-3 py-1.5 rounded border"
                              style={{ borderColor: "var(--fb-border)" }}
                              data-testid={`merge-into-other-${d.id}`}>
                        Keep other client
                      </button>
                      <button onClick={() => openDropModal(d)} disabled={busy}
                              className="text-xs font-semibold px-3 py-1.5 rounded"
                              style={{ color: "var(--fb-error)" }}
                              data-testid={`drop-${d.id}`}>
                        Delete other (no merge)
                      </button>
                    </>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
        {flags.includes("possible_duplicate") && !loadingDups && dups.length === 0 && (
          <div className="text-sm" style={{ color: "var(--fb-text-muted)" }}>
            Matched record not found — you can safely clear this flag.
          </div>
        )}

        {flags.includes("possible_member_duplicate") && (
          <div className="mt-4 space-y-2" data-testid="member-dup-section">
            <div className="text-xs uppercase tracking-wider font-semibold" style={{ color: "#8B918B" }}>
              Possible duplicate children:
            </div>
            {loadingMemberDups && (
              <div className="text-sm" style={{ color: "var(--fb-text-muted)" }}>Loading…</div>
            )}
            {!loadingMemberDups && memberDups.length === 0 && (
              <div className="flex items-center gap-3 flex-wrap">
                <span className="text-sm" style={{ color: "var(--fb-text-muted)" }}>
                  No active child matches found — this flag may be stale.
                </span>
                {!isViewer && (
                  <button
                    data-testid="clear-stale-member-dup-btn"
                    onClick={() => dismissMemberDuplicate(null)}
                    disabled={dismissingMemberPair === "stale" || busy}
                    className="text-xs font-semibold px-3 py-1 rounded-lg border hover:bg-stone-50 disabled:opacity-50"
                    style={{ borderColor: "var(--fb-border)", color: "var(--fb-text)" }}
                  >
                    {dismissingMemberPair === "stale" ? "Clearing…" : "Clear flag"}
                  </button>
                )}
              </div>
            )}
            {memberDups.map((m) => {
              const otherName = `${m.other_client_first_name || ""} ${m.other_client_last_name || ""}`.trim() || "Unknown household";
              const childName = `${m.child_first_name || ""} ${m.child_last_name || ""}`.trim() || "Unknown child";
              const isBusy = dismissingMemberPair === m.other_client_id;
              return (
                <div
                  key={m.other_client_id}
                  className="flex items-start justify-between gap-3 p-3 rounded-xl border"
                  style={{ borderColor: "var(--fb-border)", background: "#FAFAF8" }}
                  data-testid={`member-dup-row-${m.other_client_id}`}
                >
                  <div className="text-sm min-w-0 flex-1">
                    <div className="text-xs font-semibold mb-1" style={{ color: "var(--fb-text-muted)" }}>
                      Child: <span style={{ color: "var(--fb-text)" }}>{childName}</span>
                    </div>
                    <Link
                      to={`/staff/clients/${m.other_client_id}`}
                      className="font-semibold truncate block hover:underline"
                      style={{ color: "var(--fb-primary)" }}
                      data-testid={`member-dup-link-${m.other_client_id}`}
                    >
                      {otherName}
                    </Link>
                  </div>
                  {!isViewer && (
                    <button
                      data-testid={`dismiss-member-dup-${m.other_client_id}`}
                      onClick={() => dismissMemberDuplicate(m.other_client_id)}
                      disabled={isBusy || busy}
                      className="shrink-0 text-xs font-semibold px-3 py-1.5 rounded-lg border hover:bg-stone-50 disabled:opacity-50"
                      style={{ borderColor: "var(--fb-border)", color: "var(--fb-text)" }}
                    >
                      {isBusy ? "Clearing…" : "Not a duplicate"}
                    </button>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </>
  );
}

